import { useEffect, useState } from 'react';
import { ButtonField, CheckboxField } from '@wk/components-react16';
import { API_URL } from '../../config/constants';
import HttpService from '../../config/http.service';
import { Toast } from '../../services';
import AddPromptType from './model/AddPromptType';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from '../../store';
import DeleteConfirm from '../../components/DeleteConfirm';
import { Privilege } from '../../config/constants/privilege';
import { hideLoading, setAuthProject, showLoading } from '../../store/authReducer';

const PromptTypeTab = () => {
    const dispatch = useDispatch();
    const { authUser, authProject } = useSelector((state: RootState) => state.auth);
    const [promptTypeList, setPromptTypeList] = useState([] as any);
    const [defaultPromptTypeList, setDefaultPromptTypeList] = useState([] as any);
    const [isLoad, setIsLoad] = useState(false);
    const [selectedItem, setSelectedItem] = useState({} as any);
    const [isOpenAddEdit, setIsOpenAddEdit] = useState(false);
    const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);
    const [isEditPrivilege, setIsEditPrivilege] = useState(false);
    const [isDeletePrivilege, setIsDeletePrivilege] = useState(false);
    const [isChangedList, setIsChangedList] = useState([] as any);
    const [defaultList, setDefaultList] = useState([] as any);
    const [projectConfiguration, setProjectConfiguration] = useState({} as any);

    const addEdit = (item: any) => {
        setSelectedItem(item);
        setIsOpenAddEdit(true);
    }

    const setPromptProjectMapping = (selectedItem: any, isSelected: boolean) => {
        const updatedList = defaultPromptTypeList.map((item: any) => {
            if (item.id == selectedItem.id) {
                return { ...item, isSelected: isSelected };
            }
            return item;
        });
        setDefaultPromptTypeList(updatedList);
    }

    const getProjectConfiguration = () => {
        HttpService.getRequest(`${API_URL.GET_PROJECT_CONFIGURATION}/${authProject.id}`).then(
            (res: any) => {
                if (res && res.id && (res.id == authProject.id)) {
                    setProjectConfiguration(res);
                    store.dispatch(setAuthProject({ ...res }));
                }
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }

    const getPromptType = () => {
        dispatch(showLoading());
        HttpService.getRequest(`${API_URL.GET_PROMPT_TYPE_BY_PROJECT_ID}/${authProject.id}`).then(
        (res: any) => {
            dispatch(hideLoading());
            const list = res.filter((item: any) => !item.default);
            setPromptTypeList(list);
            const defaultList = res.filter((item: any) => item.default).map((item: any) => ({ ...item, isSelected: false }));
            setDefaultPromptTypeList(defaultList);
        },
        (err: any) => {
            dispatch(hideLoading());
            Toast(err.message, "error");
        })
    }

    const addPromptTypeConfiguration = () => {
        const maplist: any = defaultPromptTypeList.filter((item: any) => item.isSelected === true)
            .map((itm: any) => ({ id: itm.id, name: itm.name, code: itm.code, projectId: itm.projectId }));
        if (maplist.length === 0) {
            Toast('Please select default prompt type to map with current project.', "error");
            return
        }
        HttpService.postRequest(`${API_URL.ADD_PROMPT_TYPE_PROJECT_CONFIGURATION}/${authProject.id}`, maplist).then(
            (res: any) => {
                Toast('Default prompt type mapped successfully.', "success");
                if (res && res.id && (res.id == authProject.id)) {
                    store.dispatch(setAuthProject({ ...res }));
                }
                if (res && res.configurations) {
                    defaultPromptTypeList.forEach((item: any) => {
                        item.isSelected = res.configurations?.promptType.some((itm: any) => itm.id === item.id);
                    });
                }
                setDefaultPromptTypeList([...defaultPromptTypeList]);
            },
            (err: any) => {
                Toast(err.message, "error");
            }
        );
    }

    const handleDelete = (item: any) => {
        setIsOpenDeleteConfirm(true);
        setSelectedItem(item);
    }

    const deleteItem = () => {
        HttpService.deleteRequest(`${API_URL.DELETE_PROMPT_TYPE}/${selectedItem.id}`).then(
            (res: any) => {
                Toast("Prompt type deleted successfully.", "success");
                setIsLoad(true);
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
            },
            (err: any) => {
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
                Toast(err.message, "error");
            }
        )
    }

    useEffect(() => {
        if (isLoad) {
            getPromptType();
            setIsLoad(false);
        }
    }, [isLoad]);

    useEffect(() => {
        if (authProject && authProject.id) {
            getPromptType();
            getProjectConfiguration();
        }
    }, []);

    useEffect(() => {
        if (projectConfiguration && projectConfiguration.configurations) {
            defaultPromptTypeList.forEach((item: any) => {
                item.isSelected = projectConfiguration.configurations?.promptType.some((itm: any) => itm.id === item.id);
            });
        }
        setDefaultList([...defaultPromptTypeList]);
        setDefaultPromptTypeList([...defaultPromptTypeList]);
    }, [projectConfiguration]);

    useEffect(() => {
        if (authUser && authUser.privileges) {
            setIsEditPrivilege(authUser.privileges.includes(Privilege[Privilege.EDIT_PROMPTTYPE]));
            setIsDeletePrivilege(authUser.privileges.includes(Privilege[Privilege.DELETE_PROMPTTYPE]));
        } else {
            setIsEditPrivilege(false);
            setIsDeletePrivilege(false);
        }
    }, [authUser]);


    useEffect(() => {
        const mismatchedItems = defaultPromptTypeList.filter((item: any) => {
            const updatedItem = defaultList.find((u: any) => u.id === item.id);
            return updatedItem && item.isSelected !== updatedItem.isSelected;
        });
        setIsChangedList([...mismatchedItems]);
    }, [defaultPromptTypeList, defaultList]);

    return (
        <div className="p-20 pt-10 wk-card-container mb-0 rounded-3 box-shadow">
            {authProject?.code?.toUpperCase() !== 'WK' && (<>
                <div className="wk-row">
                    <div className="wk-col-6">
                        <h6 className="wk-weight-400 mt-0">WK Default Prompt Type</h6>
                    </div>
                    <div className="wk-col-6 text-right">
                        {isEditPrivilege && (
                            <ButtonField size={'small'} mode={isChangedList.length > 0 ? 'default' : 'ghost'}>
                                <button type="button" className="mr-5" onClick={() => addPromptTypeConfiguration()}>
                                    Select Prompt Type
                                </button>
                            </ButtonField>
                        )}
                    </div>
                </div>
                <div className="wk-row mt-10 mb-20">
                    <div className="wk-col-12">
                        <table className="wk-table wk-table-bordered wk-table-small h-100">
                            <thead>
                                <tr>
                                    <th>Prompt Type Name</th>
                                    <th style={{ width: '30%' }}>Code</th>
                                    <th style={{ width: '15%' }}>GPT Agent</th>
                                    {isEditPrivilege && (<th style={{ width: '80px' }}>Actions</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {defaultPromptTypeList.length > 0 ? defaultPromptTypeList.map((item: any, index: number) => (
                                    <tr key={index}>
                                        <td>{item.name}</td>
                                        <td>{item.code}</td>
                                        <td>{item.gptAgent}</td>
                                        {isEditPrivilege && (
                                            <td>
                                                <div className='custom-fs-field'>
                                                    <CheckboxField label="">
                                                        <input
                                                            name="checkbox1"
                                                            type="checkbox"
                                                            disabled={!item.default}
                                                            checked={item.isSelected}
                                                            onChange={(event: any) => setPromptProjectMapping(item, event.target.checked)}
                                                        />
                                                    </CheckboxField>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={isEditPrivilege ? 3 : 2} className="text-center" style={{ height: '7rem' }}>
                                            No record found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>)}
            <div className="wk-row">
                <div className="wk-col-6">
                    <h6 className="wk-weight-400 mt-0">Project Specific Prompt Type</h6>
                </div>
                <div className="wk-col-6 text-right">
                    {isEditPrivilege && (
                        <ButtonField size={'small'} mode={'ghost'}>
                            <button type="button" className="mr-5" onClick={() => addEdit({})}>
                                Add Prompt Type
                            </button>
                        </ButtonField>
                    )}
                </div>
            </div>
            <div className="wk-row mt-10">
                <div className="wk-col-12">
                    <table className="wk-table wk-table-bordered wk-table-small h-100">
                        <thead>
                            <tr>
                                <th>Prompt Type Name</th>
                                <th style={{ width: '30%' }}>Code</th>
                                <th style={{ width: '15%' }}>GPT Agent</th>
                                {(isEditPrivilege || isDeletePrivilege) && <th style={{ width: '80px' }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {promptTypeList.length > 0 ? promptTypeList.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>{item.code}</td>
                                    <td>{item.gptAgent}</td>
                                    {(isEditPrivilege || isDeletePrivilege) && (
                                        <td>
                                            {isEditPrivilege && <span className="wk-icon-pencil mr-10 cursor" title="Edit"
                                                onClick={() => addEdit(item)}></span>}
                                            {isDeletePrivilege && <span className="wk-icon-trash cursor" title="Delete" onClick={() => handleDelete(item)}></span>}
                                        </td>
                                    )}
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={(isEditPrivilege || isDeletePrivilege) ? 3 : 2} className="text-center" style={{ height: '7rem' }}>
                                        No record found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {isOpenAddEdit && <AddPromptType data={{ isOpenAddEdit, setIsOpenAddEdit, selectedItem, setIsLoad }} />}
            {isOpenDeleteConfirm && <DeleteConfirm data={{ isOpenDeleteConfirm, setIsOpenDeleteConfirm, deleteItem, message: `Are you sure you want to delete?` }} />}
        </div>
    );
};

export default PromptTypeTab;